import React, { useMemo, useState } from "react";
import { Select, Tag, Tooltip, Progress, Pagination } from "antd";
import {
  TrophyOutlined,
  DollarOutlined,
  StarOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import {
  colors,
  chartGradients,
  getSupplierInitials,
  formatMYR,
  MaterialInfo,
} from "./comparisonUtils";

interface MaterialAnalysisPanelProps {
  suppliers: any[];
  evaluationScoreMap: Record<string, any>;
  materialNames: MaterialInfo[];
}

const MaterialAnalysisPanel: React.FC<MaterialAnalysisPanelProps> = ({
  suppliers,
  evaluationScoreMap,
  materialNames,
}) => {
  const [selectedMaterial, setSelectedMaterial] = useState<string>(
    "overview"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Build per-material supplier data (keyed by tenderMaterialId)
  const materialSupplierData = useMemo(() => {
    const result: Record<
      number,
      {
        supplierID: string;
        supplierName: string;
        unitPrice: number;
        totalPrice: number;
        supplyQty: number;
        totalInstallationCharges: number;
        discountAmount: number;
        evalScore: number;
        rank: number;
        paymentTerms: string;
        colorIndex: number;
      }[]
    > = {};

    materialNames.forEach((mat) => {
      const entries = suppliers
        .map((supplier, idx) => {
          const li = (supplier.lineItems || []).find(
            (l: any) => l.tenderMaterialId === mat.tenderMaterialId
          );
          if (!li) return null;
          const evalData = evaluationScoreMap[supplier.supplierID];
          const installCharges = li.totalInstallationCharges || 0;
          const discount = li.discountAmount || 0;
          return {
            supplierID: supplier.supplierID,
            supplierName: supplier.supplierName,
            unitPrice: li.quotedUnitPrice,
            totalPrice: li.quotedQuantity * li.quotedUnitPrice + installCharges - discount,
            supplyQty: li.quotedQuantity,
            totalInstallationCharges: installCharges,
            discountAmount: discount,
            evalScore: evalData?.overallScore ?? 0,
            rank: evalData?.rank ?? 99,
            paymentTerms: supplier.paymentTerms,
            colorIndex: idx,
          };
        })
        .filter(Boolean) as any[];
      result[mat.tenderMaterialId] = entries.sort((a, b) => a.totalPrice - b.totalPrice);
    });
    return result;
  }, [suppliers, evaluationScoreMap, materialNames]);

  // Overview: build heatmap data — per material, min/max prices & spread
  const heatmapData = useMemo(() => {
    return materialNames.map((mat) => {
      const entries = materialSupplierData[mat.tenderMaterialId] || [];
      const prices = entries.map((e) => e.totalPrice);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const spread = maxPrice - minPrice;
      const spreadPct = maxPrice > 0 ? (spread / minPrice) * 100 : 0;
      const bestScoreEntry = [...entries].sort(
        (a, b) => b.evalScore - a.evalScore
      )[0];
      return {
        tenderMaterialId: mat.tenderMaterialId,
        materialName: mat.materialName,
        usage: mat.usage || "-",
        entries,
        minPrice,
        maxPrice,
        spread,
        spreadPct,
        lowestPriceID: entries[0]?.supplierID,
        bestScoreID: bestScoreEntry?.supplierID,
      };
    });
  }, [materialNames, materialSupplierData]);

  const getCellBg = (
    price: number,
    min: number,
    max: number
  ): string => {
    if (max === min) return "#f0fdf4";
    const ratio = (price - min) / (max - min);
    if (ratio < 0.01) return "#dcfce7"; // lowest — green
    if (ratio < 0.33) return "#f0fdf4"; // low — very light green
    if (ratio < 0.66) return "#fefce8"; // mid — light yellow
    return "#fef2f2"; // high — light red
  };

  // Spread severity color helper
  const getSpreadColor = (pct: number) => {
    if (pct < 5) return { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d", label: "Tight" };
    if (pct < 15) return { bg: "#fffbeb", border: "#fde68a", text: "#a16207", label: "Moderate" };
    if (pct < 30) return { bg: "#fff7ed", border: "#fed7aa", text: "#c2410c", label: "Wide" };
    return { bg: "#fef2f2", border: "#fecaca", text: "#dc2626", label: "Very Wide" };
  };

  // Material detail view
  const renderMaterialDetail = () => {
    const selectedId = Number(selectedMaterial);
    const entries = materialSupplierData[selectedId] || [];
    if (entries.length === 0) return null;

    const maxTotalPrice = Math.max(...entries.map((e) => e.totalPrice));
    const minTotalPrice = Math.min(...entries.map((e) => e.totalPrice));
    const spread = maxTotalPrice - minTotalPrice;
    const spreadPct = minTotalPrice > 0 ? (spread / minTotalPrice) * 100 : 0;
    const spreadStyle = getSpreadColor(spreadPct);
    const lowestPrice = entries[0];
    const bestScore = [...entries].sort(
      (a, b) => b.evalScore - a.evalScore
    )[0];
    const bestValue = [...entries].sort((a, b) => {
      // Score price: normalize both to 0-100 and combine (60% price, 40% score)
      const priceScoreA =
        maxTotalPrice > 0
          ? ((maxTotalPrice - a.totalPrice) / maxTotalPrice) * 100
          : 0;
      const priceScoreB =
        maxTotalPrice > 0
          ? ((maxTotalPrice - b.totalPrice) / maxTotalPrice) * 100
          : 0;
      const valA = priceScoreA * 0.6 + a.evalScore * 0.4;
      const valB = priceScoreB * 0.6 + b.evalScore * 0.4;
      return valB - valA;
    })[0];

    return (
      <div>
        {/* Back link */}
        <a
          onClick={() => setSelectedMaterial("overview")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 12,
            fontSize: 13,
            color: "#5B93D3",
            cursor: "pointer",
          }}
        >
          <ArrowLeftOutlined style={{ fontSize: 12 }} />
          Back to Overview
        </a>

        {/* Quick badges */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 8,
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
            }}
          >
            <DollarOutlined style={{ color: "#16a34a" }} />
            <span style={{ color: "#666" }}>Lowest Price:</span>
            <b style={{ color: "#15803d" }}>{lowestPrice.supplierName}</b>
            <span style={{ color: "#888" }}>
              INR {formatMYR(lowestPrice.totalPrice)}
            </span>
          </div>
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              padding: "8px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
            }}
          >
            <StarOutlined style={{ color: "#2563eb" }} />
            <span style={{ color: "#666" }}>Top Evaluated:</span>
            <b style={{ color: "#1d4ed8" }}>{bestScore.supplierName}</b>
            <span style={{ color: "#888" }}>
              {bestScore.evalScore.toFixed(1)}/100
            </span>
          </div>
          {bestValue.supplierID !== lowestPrice.supplierID &&
            bestValue.supplierID !== bestScore.supplierID && (
              <div
                style={{
                  background: "#faf5ff",
                  border: "1px solid #e9d5ff",
                  borderRadius: 8,
                  padding: "8px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <TrophyOutlined style={{ color: "#9333ea" }} />
                <span style={{ color: "#666" }}>Best Value:</span>
                <b style={{ color: "#7e22ce" }}>{bestValue.supplierName}</b>
              </div>
            )}
        </div>

        {/* Spread Summary Card */}
        {entries.length >= 2 && (
          <div
            style={{
              marginBottom: 16,
              padding: "12px 16px",
              background: spreadStyle.bg,
              border: `1px solid ${spreadStyle.border}`,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: spreadStyle.text,
                  background: "#fff",
                  border: `1px solid ${spreadStyle.border}`,
                  borderRadius: 4,
                  padding: "2px 8px",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {spreadStyle.label} Spread
              </div>
            </div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#888" }}>Lowest</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#15803d" }}>
                  INR {formatMYR(minTotalPrice)}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#888" }}>Highest</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>
                  INR {formatMYR(maxTotalPrice)}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#888" }}>Spread</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: spreadStyle.text }}>
                  INR {formatMYR(spread)}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#888" }}>Spread %</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: spreadStyle.text }}>
                  {spreadPct.toFixed(1)}%
                </div>
              </div>
            </div>
            {/* Visual spread bar */}
            <div style={{ flex: 1, minWidth: 120 }}>
              <div
                style={{
                  position: "relative",
                  height: 8,
                  background: "#e5e7eb",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${Math.min(spreadPct, 100)}%`,
                    borderRadius: 4,
                    background: `linear-gradient(90deg, #22c55e, ${spreadStyle.text})`,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Supplier ranking bars */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {entries.map((entry, idx) => {
            const pctOfMax =
              maxTotalPrice > 0
                ? (entry.totalPrice / maxTotalPrice) * 100
                : 0;
            const [gradBase, gradLight] =
              chartGradients[entry.colorIndex % chartGradients.length];
            const isLowest = idx === 0;
            const isBestScore =
              entry.supplierID === bestScore.supplierID;

            return (
              <div
                key={entry.supplierID}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: isLowest
                    ? "#f8fdf9"
                    : "#fafafa",
                  border: isLowest
                    ? "1px solid #d1fae5"
                    : "1px solid #f0f0f0",
                  transition: "all 0.2s",
                }}
              >
                {/* Rank number */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background:
                      idx === 0
                        ? "#dcfce7"
                        : idx === 1
                        ? "#fef3c7"
                        : idx === 2
                        ? "#ffedd5"
                        : "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    color:
                      idx === 0
                        ? "#15803d"
                        : idx === 1
                        ? "#a16207"
                        : idx === 2
                        ? "#c2410c"
                        : "#888",
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>

                {/* Avatar */}
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor:
                      colors[entry.colorIndex % colors.length],
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {getSupplierInitials(entry.supplierName)}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#333",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entry.supplierName}
                    </span>
                    {isLowest && (
                      <Tag
                        color="green"
                        style={{ fontSize: 10, margin: 0, lineHeight: "18px" }}
                      >
                        Lowest Price
                      </Tag>
                    )}
                    {isBestScore && (
                      <Tag
                        color="blue"
                        style={{ fontSize: 10, margin: 0, lineHeight: "18px" }}
                      >
                        Top Score
                      </Tag>
                    )}
                  </div>

                  {/* Price bar */}
                  <div
                    style={{
                      position: "relative",
                      height: 8,
                      background: "#f0f0f0",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${pctOfMax}%`,
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${gradBase}, ${gradLight})`,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                </div>

                {/* Metrics */}
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <Tooltip title="Unit Price">
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Unit Price
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {formatMYR(entry.unitPrice)}
                      </div>
                    </div>
                  </Tooltip>
                  <Tooltip title="Installation Charges">
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Installation
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {formatMYR(entry.totalInstallationCharges)}
                      </div>
                    </div>
                  </Tooltip>
                  <Tooltip title="Discount Amount">
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Discount
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: entry.discountAmount > 0 ? "#d4380d" : "#555",
                        }}
                      >
                        {entry.discountAmount > 0 ? formatMYR(entry.discountAmount) : "-"}
                      </div>
                    </div>
                  </Tooltip>
                  <Tooltip title="Total Price (Qty × Unit Price + Installation - Discount)">
                    <div style={{ textAlign: "right", minWidth: 90 }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Total Price
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: isLowest ? "#15803d" : "#333",
                        }}
                      >
                        {formatMYR(entry.totalPrice)}
                      </div>
                    </div>
                  </Tooltip>
                  <Tooltip title="Spread from lowest price">
                    <div style={{ textAlign: "right", minWidth: 72 }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Spread
                      </div>
                      {isLowest ? (
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#15803d",
                          }}
                        >
                          Base
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color:
                              ((entry.totalPrice - minTotalPrice) / minTotalPrice) * 100 > 15
                                ? "#dc2626"
                                : ((entry.totalPrice - minTotalPrice) / minTotalPrice) * 100 > 5
                                ? "#a16207"
                                : "#555",
                          }}
                        >
                          +{formatMYR(entry.totalPrice - minTotalPrice)}{" "}
                          <span style={{ fontSize: 10, fontWeight: 400 }}>
                            ({(((entry.totalPrice - minTotalPrice) / minTotalPrice) * 100).toFixed(1)}%)
                          </span>
                        </div>
                      )}
                    </div>
                  </Tooltip>
                  <Tooltip title="Evaluation Score">
                    <div style={{ textAlign: "center", minWidth: 52 }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Eval Score
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: isBestScore ? "#1d4ed8" : "#555",
                        }}
                      >
                        {entry.evalScore > 0
                          ? entry.evalScore.toFixed(1)
                          : "-"}
                      </div>
                    </div>
                  </Tooltip>
                  <Tooltip title="Payment Terms">
                    <div style={{ textAlign: "center", minWidth: 52 }}>
                      <div
                        style={{ fontSize: 10, color: "#aaa", lineHeight: 1 }}
                      >
                        Payment
                      </div>
                      <div style={{ fontSize: 12, color: "#666" }}>
                        <ClockCircleOutlined
                          style={{ fontSize: 10, marginRight: 2 }}
                        />
                        {entry.paymentTerms}
                      </div>
                    </div>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>

        {/* Savings insight */}
        {entries.length >= 2 && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 14px",
              background: "#f8fafc",
              borderRadius: 8,
              fontSize: 12,
              color: "#666",
              border: "1px solid #e2e8f0",
            }}
          >
            Choosing <b>{entries[0].supplierName}</b> over{" "}
            <b>{entries[entries.length - 1].supplierName}</b> saves{" "}
            <b style={{ color: "#15803d" }}>
              INR{" "}
              {formatMYR(
                entries[entries.length - 1].totalPrice -
                  entries[0].totalPrice
              )}
            </b>{" "}
            ({(
              ((entries[entries.length - 1].totalPrice -
                entries[0].totalPrice) /
                entries[entries.length - 1].totalPrice) *
              100
            ).toFixed(1)}
            % less) on this material.
          </div>
        )}
      </div>
    );
  };

  // Overview: heatmap table
  const renderOverview = () => (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: "0 4px",
          fontSize: 13,
        }}
      >
        <colgroup>
          <col style={{ width: 220 }} />
          <col style={{ width: 150 }} />
          {suppliers.map((s) => (
            <col key={s.supplierID} style={{ width: 130 }} />
          ))}
          <col style={{ width: 80 }} />
          <col style={{ width: 110 }} />
        </colgroup>
        <thead>
          <tr>
            <th
              style={{
                textAlign: "left",
                padding: "8px 12px",
                color: "#888",
                fontWeight: 500,
                fontSize: 12,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              Material
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "8px 12px",
                color: "#888",
                fontWeight: 500,
                fontSize: 12,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              Usage
            </th>
            {suppliers.map((s, idx) => (
              <th
                key={s.supplierID}
                style={{
                  textAlign: "center",
                  padding: "8px 6px",
                  color: "#666",
                  fontWeight: 500,
                  fontSize: 11,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      backgroundColor: colors[idx % colors.length],
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {getSupplierInitials(s.supplierName)}
                  </div>
                  <span>{s.supplierName}</span>
                </div>
              </th>
            ))}
            <th
              style={{
                textAlign: "center",
                padding: "8px 12px",
                color: "#888",
                fontWeight: 500,
                fontSize: 12,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              Lowest
            </th>
            <th
              style={{
                textAlign: "center",
                padding: "8px 12px",
                color: "#888",
                fontWeight: 500,
                fontSize: 12,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              Spread
            </th>
          </tr>
        </thead>
        <tbody>
          {heatmapData
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((row) => (
            <tr
              key={row.tenderMaterialId}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedMaterial(String(row.tenderMaterialId))}
            >
              <td
                style={{
                  padding: "8px 12px",
                  fontWeight: 500,
                  color: "#333",
                  borderRadius: "8px 0 0 8px",
                  background: "#fafafa",
                  wordBreak: "break-word",
                }}
              >
                {row.materialName}
              </td>
              <td
                style={{
                  padding: "8px 12px",
                  color: "#555",
                  fontSize: 12,
                  background: "#fafafa",
                  wordBreak: "break-word",
                  maxWidth: 150,
                }}
              >
                {row.usage && row.usage.length > 20 ? (
                  <Tooltip title={row.usage}>
                    <span style={{ cursor: "pointer" }}>{row.usage.slice(0, 20)}...</span>
                  </Tooltip>
                ) : (
                  row.usage
                )}
              </td>
              {suppliers.map((s) => {
                const entry = row.entries.find(
                  (e) => e.supplierID === s.supplierID
                );
                if (!entry) {
                  return (
                    <td
                      key={s.supplierID}
                      style={{
                        textAlign: "center",
                        padding: "8px 6px",
                        color: "#ccc",
                        background: "#fcfcfc",
                      }}
                    >
                      -
                    </td>
                  );
                }
                const bg = getCellBg(
                  entry.totalPrice,
                  row.minPrice,
                  row.maxPrice
                );
                const isLowestPrice =
                  entry.supplierID === row.lowestPriceID;
                return (
                  <td
                    key={s.supplierID}
                    style={{
                      textAlign: "center",
                      padding: "8px 6px",
                      background: bg,
                      fontWeight: isLowestPrice ? 700 : 400,
                      color: isLowestPrice ? "#15803d" : "#555",
                      transition: "background 0.2s",
                    }}
                  >
                    {formatMYR(entry.totalPrice)}
                    {isLowestPrice && (
                      <div style={{ fontSize: 9, color: "#16a34a" }}>
                        lowest
                      </div>
                    )}
                  </td>
                );
              })}
              <td
                style={{
                  textAlign: "center",
                  padding: "8px 12px",
                  background: "#f0fdf4",
                }}
              >
                <Tooltip
                  title={
                    row.entries[0]
                      ? row.entries[0].supplierName
                      : "-"
                  }
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#15803d",
                    }}
                  >
                    {row.entries[0]
                      ? getSupplierInitials(
                          row.entries[0].supplierName
                        )
                      : "-"}
                  </div>
                </Tooltip>
              </td>
              <td
                style={{
                  textAlign: "center",
                  padding: "8px 8px",
                  borderRadius: "0 8px 8px 0",
                  background: row.entries.length >= 2
                    ? getSpreadColor(row.spreadPct).bg
                    : "#fafafa",
                  border: row.entries.length >= 2
                    ? `1px solid ${getSpreadColor(row.spreadPct).border}`
                    : undefined,
                }}
              >
                {row.entries.length >= 2 ? (
                  <Tooltip
                    title={`Range: INR ${formatMYR(row.minPrice)} – ${formatMYR(row.maxPrice)}`}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: getSpreadColor(row.spreadPct).text,
                        }}
                      >
                        {row.spreadPct.toFixed(1)}%
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: getSpreadColor(row.spreadPct).text,
                          opacity: 0.8,
                        }}
                      >
                        {getSpreadColor(row.spreadPct).label}
                      </div>
                    </div>
                  </Tooltip>
                ) : (
                  <span style={{ color: "#ccc" }}>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        style={{
          fontSize: 11,
          color: "#aaa",
          marginTop: 8,
          textAlign: "center",
        }}
      >
        Click any material row to see detailed supplier ranking
      </div>
      {heatmapData.length > pageSize && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={heatmapData.length}
            showSizeChanger
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            size="small"
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        marginTop: 16,
        background: "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 10,
        padding: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 15, color: "#333" }}>
          Material Analysis
        </div>
        <Select
          value={selectedMaterial}
          onChange={setSelectedMaterial}
          style={{ minWidth: 280 }}
          placeholder="Select material"
        >
          <Select.Option value="overview">
            Overview — Price Heatmap
          </Select.Option>
          {materialNames.map((mat) => (
            <Select.Option key={mat.tenderMaterialId} value={String(mat.tenderMaterialId)}>
              {mat.materialName}{mat.usage ? ` — ${mat.usage}` : ""}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Content */}
      {selectedMaterial === "overview"
        ? renderOverview()
        : renderMaterialDetail()}
    </div>
  );
};

export default MaterialAnalysisPanel;
